import { DownloadService } from "@/services/admin-services/DownloadService";
import { Download } from "@/services/admin-services/models/Download";
import { LocaleService } from "@/services/global-services";
import { useDataStore } from "@/stores";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    TextField,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { CanceledError } from "axios";
import { FormEvent, useEffect, useMemo, useState } from "react";
import DownloadDisciplinesDropdown from "./components/DownloadDisciplinesDropdown";
import DownloadSoftwareDropdown from "./components/DownloadSoftwareDropdown";
import DownloadUseCasesDropdown from "./components/DownloadUseCasesDropdown";
import DownloadVersionsDropdown from "./components/DownloadVersionsDropdown";

interface EditDialogProps {
    download: Download | null;
    onClose: () => void;
    onSubmit: (file: Download) => void;
}

export const EditDialog = ({ download, onClose, onSubmit }: EditDialogProps) => {
    const [versionDataLoading, setVersionDataLoading] = useState<boolean>(false);

    const disciplines = useDataStore((state) => state.disciplines);
    const software = useDataStore((state) => state.software);
    const useCases = useDataStore((state) => state.useCases);

    const setDisciplines = useDataStore((state) => state.setDisciplines);
    const setSoftware = useDataStore((state) => state.setSoftware);
    const setUseCases = useDataStore((state) => state.setUseCases);

    const [filename, setFilename] = useState<string>("");
    const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
    const [selectedDisciplines, setSelectedDisciplines] = useState<Array<string>>([]);
    const [selectedSoftware, setSelectedSoftware] = useState<Array<string>>([]);
    const [selectedUseCases, setSelectedUseCases] = useState<Array<string>>([]);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const downloadService = new DownloadService("int");
    const localeService = useMemo(() => new LocaleService("int", "en"), []);

    useEffect(() => {
        if (download) {
            setFilename(download.name);
            setSelectedVersions(download.versions);
            setSelectedDisciplines(download.disciplines || []);
            setSelectedSoftware(download.software || []);
            setSelectedUseCases(download.useCases || []);
        }
    }, [download]);

    useEffect(() => {
        if (!selectedVersions) return;

        setVersionDataLoading(true);

        const promises: Promise<void>[] = [];

        selectedVersions.forEach((selectedVersion) => {
            if (disciplines[selectedVersion] && software[selectedVersion] && useCases[selectedVersion]) return;

            promises.push(
                Promise.all([
                    localeService.getDisciplines(selectedVersion),
                    localeService.getSoftware(selectedVersion),
                    localeService.getUseCases(selectedVersion),
                ]).then(([disciplines, software, useCases]) => {
                    setDisciplines(selectedVersion, disciplines);
                    setSoftware(selectedVersion, software);
                    setUseCases(selectedVersion, useCases);
                })
            );
        });

        Promise.all(promises).then(() => setVersionDataLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVersions]);

    const onFormSubmit = (event: FormEvent) => {
        event.preventDefault();

        if (!filename) return;

        setIsSaving(true);

        downloadService
            .update(download!.id, download?.languageCode || "en", {
                name: filename,
                versions: selectedVersions,
                disciplines: selectedDisciplines,
                software: selectedSoftware,
                useCases: selectedUseCases,
            })
            .then((file) => {
                setIsSaving(false);
                onSubmit(file);
            })
            .catch((err) => {
                if (err instanceof CanceledError) return;
                throw err;
            });
    };

    return (
        <Dialog open={Boolean(download)} onClose={onClose} fullWidth maxWidth={"sm"} fullScreen={fullScreen}>
            <form onSubmit={(event) => onFormSubmit(event)}>
                <DialogTitle>Edit download</DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        pointerEvents: isSaving ? "none" : "auto",
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                autoFocus
                                fullWidth
                                id={`download-filename`}
                                value={filename}
                                onChange={(event) => setFilename(event.target.value)}
                                label={`Filename`}
                                type="text"
                                variant="outlined"
                                disabled={isSaving}
                            />
                        </Grid>
                    </Grid>
                    <Divider sx={{ marginY: 3 }} />
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <DownloadVersionsDropdown
                                value={selectedVersions}
                                onChange={(e) => setSelectedVersions(e.target.value as string[])}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <DownloadDisciplinesDropdown
                                selectedVersions={selectedVersions}
                                value={selectedDisciplines}
                                onChange={(e) => setSelectedDisciplines(e.target.value as string[])}
                                loading={versionDataLoading}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <DownloadSoftwareDropdown
                                selectedVersions={selectedVersions}
                                value={selectedSoftware}
                                onChange={(e) => setSelectedSoftware(e.target.value as string[])}
                                loading={versionDataLoading}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <DownloadUseCasesDropdown
                                selectedVersions={selectedVersions}
                                value={selectedUseCases}
                                onChange={(e) => setSelectedUseCases(e.target.value as string[])}
                                loading={versionDataLoading}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button variant="contained" type="submit" disabled={isSaving}>
                        Update
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
